import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'constants/AppRoutes';
import { USERS_ROLE } from 'constants/interfaces';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import LaunchpadSplash from './components/LaunchpadSplash/LaunchpadSplash';
import useDiscoverNavigation from './hooks/useDiscoverNavigation';
import useLaunchpadActiveValueChainMap from './hooks/useLaunchpadActiveValueChainMap';
import useLaunchpadSplash from './hooks/useLaunchpadSplash';
import LaunchpadHubView from './LaunchpadHubView';

import type { ReactElement } from 'react';

function LaunchpadHub(): ReactElement | null {
  const navigate = useNavigate();
  const { isFetching, data } = useUserMeQuery();
  const {
    role, company, firstName, lastName,
  } = data?.data || {};
  const isSuperAdmin = role === USERS_ROLE.admin;
  const { splashPhase } = useLaunchpadSplash();
  const userDisplayName = [firstName, lastName].filter(Boolean).join(' ').trim() || firstName;
  const {
    options: valueChainMapOptions,
    maps: valueChainMaps,
    selectedMapId,
    selectedMap,
    setSelectedMapId,
    isLoading: isValueChainMapsLoading,
    isEmpty: isValueChainMapsEmpty,
    hasActiveVcm,
  } = useLaunchpadActiveValueChainMap();
  const navigateToDiscover = useDiscoverNavigation(selectedMapId);
  const { companyLogo } = company?.uiConfiguration || {};

  useEffect(() => {
    if (isSuperAdmin) {
      navigate(AppRoutes.clients);
    }
  }, [isSuperAdmin, navigate]);

  // No value chain maps to work with — send the user to the value chain list instead.
  useEffect(() => {
    if (!isSuperAdmin && isValueChainMapsEmpty) {
      navigate(AppRoutes.valueChainMap);
    }
  }, [isSuperAdmin, isValueChainMapsEmpty, navigate]);

  return (
    <>
      <LaunchpadSplash
        splashPhase={splashPhase}
        companyName={company?.name}
        companyLogo={companyLogo}
        userName={userDisplayName}
      />
      <LaunchpadHubView
        isLoading={isFetching}
        hasActiveVcm={hasActiveVcm}
        valueChainMapOptions={valueChainMapOptions}
        valueChainMaps={valueChainMaps}
        selectedMapId={selectedMapId}
        selectedMap={selectedMap}
        onSelectMapId={setSelectedMapId}
        isValueChainMapsLoading={isValueChainMapsLoading}
        isValueChainMapsEmpty={isValueChainMapsEmpty}
        onDiscoverClick={navigateToDiscover}
      />
    </>
  );
}

export default LaunchpadHub;
